/**
 * Home Assistant API Client
 *
 * Frontend API calls to the backend HA integration endpoints.
 * All calls go through the main TerraMatrix backend which relays
 * to the HA add-on bridge via Socket.IO.
 */

import { apiClient } from './client';
import type { HAAutomationConfig, HAAutomationListItem } from '@/types/haAutomation';

// apiClient already includes /api/v1 in its baseURL; use a relative path that
// starts after that to avoid a doubled /api/v1 prefix in resolved URLs.
const HA_BASE = '/integrations/homeassistant';

// ── Types ──

export interface HAInstance {
  _id: string;
  orgId: string;
  instanceName: string;
  // Mirrors the backend HAInstance.status enum (online/offline/pending).
  status: 'online' | 'offline' | 'pending';
  lastSeen: string;
  entityCount: number;
  haVersion?: string;
  addOnVersion?: string;
}

export interface HAEntity {
  entity_id: string;
  state: string;
  attributes: Record<string, unknown>;
  last_changed: string;
  last_updated: string;
  domain: string;
  device?: {
    id: string;
    name: string;
    manufacturer: string | null;
    model: string | null;
    area_id: string | null;
  } | null;
  area?: {
    area_id: string;
    name: string;
    floor_id: string | null;
  } | null;
}

export interface HAEntityName {
  entityId: string;
  displayName: string;
  semanticType?: string;
  updatedAt?: string;
}

export interface HAArea {
  area_id: string;
  name: string;
  floor_id: string | null;
  entities: HAEntity[];
  devices: Array<{
    id: string;
    name: string;
    manufacturer: string | null;
    model: string | null;
  }>;
}

export interface HAAutomation {
  id: string;
  alias: string;
  state: 'on' | 'off';
  last_triggered: string | null;
  mode: string;
}

export interface HAHistoryPoint {
  timestamp: string;
  value: number | string;
  numericValue?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function getOptionalString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}

function getOptionalNullableString(
  record: Record<string, unknown>,
  key: string,
): string | null | undefined {
  const value = record[key];
  if (value === null) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function getOptionalRecord(
  record: Record<string, unknown>,
  key: string,
): Record<string, unknown> | undefined {
  const value = record[key];
  return isRecord(value) ? value : undefined;
}

function getEntityDomain(entityId: string): string {
  const [domain] = entityId.split('.');
  return domain ?? '';
}

function parseEntityDevice(value: unknown): HAEntity['device'] | undefined {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    return undefined;
  }

  const id = getOptionalString(value, 'id');
  const name = getOptionalString(value, 'name');
  const manufacturer = getOptionalNullableString(value, 'manufacturer');
  const model = getOptionalNullableString(value, 'model');
  const areaId = getOptionalNullableString(value, 'area_id');

  if (!id || !name || manufacturer === undefined || model === undefined || areaId === undefined) {
    return undefined;
  }

  return {
    id,
    name,
    manufacturer,
    model,
    area_id: areaId,
  };
}

function parseEntityArea(value: unknown): HAEntity['area'] | undefined {
  if (value === null) {
    return null;
  }
  if (!isRecord(value)) {
    return undefined;
  }

  const areaId = getOptionalString(value, 'area_id');
  const name = getOptionalString(value, 'name');
  const floorId = getOptionalNullableString(value, 'floor_id');

  if (!areaId || !name || floorId === undefined) {
    return undefined;
  }

  return {
    area_id: areaId,
    name,
    floor_id: floorId,
  };
}

function normalizeEntityPayload(payload: unknown): HAEntity | null {
  if (!isRecord(payload)) {
    return null;
  }

  const entityId = getOptionalString(payload, 'entity_id') ?? getOptionalString(payload, 'entityId');
  const state = getOptionalString(payload, 'state');
  const attributes = getOptionalRecord(payload, 'attributes');
  const lastChanged =
    getOptionalString(payload, 'last_changed') ?? getOptionalString(payload, 'lastChanged');
  const lastUpdated =
    getOptionalString(payload, 'last_updated') ??
    getOptionalString(payload, 'lastUpdated') ??
    lastChanged;

  if (!entityId || !state || !attributes || !lastChanged || !lastUpdated) {
    return null;
  }

  const device = parseEntityDevice(payload.device);
  const area = parseEntityArea(payload.area);

  return {
    entity_id: entityId,
    state,
    attributes,
    last_changed: lastChanged,
    last_updated: lastUpdated,
    domain: getOptionalString(payload, 'domain') ?? getEntityDomain(entityId),
    ...(device !== undefined ? { device } : {}),
    ...(area !== undefined ? { area } : {}),
  };
}

function extractEntityCollectionPayload(payload: unknown): unknown {
  if (!isRecord(payload)) {
    return payload;
  }

  if (payload.entities !== undefined) {
    return payload.entities;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  return payload;
}

function normalizeEntityCollectionPayload(payload: unknown): HAEntity[] {
  const extractedPayload = extractEntityCollectionPayload(payload);
  if (!Array.isArray(extractedPayload)) {
    return [];
  }

  const entities: HAEntity[] = [];
  for (const entityPayload of extractedPayload) {
    const entity = normalizeEntityPayload(entityPayload);
    if (entity !== null) {
      entities.push(entity);
    }
  }

  return entities;
}

function extractEntityPayload(payload: unknown): unknown {
  if (!isRecord(payload)) {
    return payload;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  if (payload.entity !== undefined) {
    return payload.entity;
  }

  return payload;
}

// ── Instance Management ──

export async function getInstances(): Promise<HAInstance[]> {
  const response = await apiClient.get(`${HA_BASE}/instances`);
  return response.data?.instances || response.data || [];
}

export async function registerInstance(instanceName: string): Promise<{ instance: HAInstance; cloudToken: string }> {
  const response = await apiClient.post(`${HA_BASE}/instances`, { instanceName });
  return response.data;
}

export async function removeInstance(instanceId: string): Promise<void> {
  await apiClient.delete(`${HA_BASE}/instances/${instanceId}`);
}

// ── Entity Operations ──

export async function getEntities(instanceId: string, domain?: string): Promise<HAEntity[]> {
  const params = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  const response = await apiClient.get(`${HA_BASE}/instances/${instanceId}/entities${params}`);
  return normalizeEntityCollectionPayload(response.data);
}

export async function getEntityState(instanceId: string, entityId: string): Promise<HAEntity | null> {
  const response = await apiClient.get(
    `${HA_BASE}/instances/${instanceId}/entities/${encodeURIComponent(entityId)}`
  );
  return normalizeEntityPayload(extractEntityPayload(response.data));
}

export async function getEntityNames(instanceId: string): Promise<HAEntityName[]> {
  const response = await apiClient.get(`${HA_BASE}/instances/${instanceId}/entity-names`);
  return response.data?.names || response.data || [];
}

export async function setEntityName(
  instanceId: string,
  entityId: string,
  displayName: string,
  semanticType?: string
): Promise<HAEntityName> {
  const response = await apiClient.put(
    `${HA_BASE}/instances/${instanceId}/entity-names/${encodeURIComponent(entityId)}`,
    { displayName, semanticType }
  );
  return response.data?.name || response.data;
}

export async function deleteEntityName(instanceId: string, entityId: string): Promise<void> {
  await apiClient.delete(
    `${HA_BASE}/instances/${instanceId}/entity-names/${encodeURIComponent(entityId)}`
  );
}

export async function getAreas(instanceId: string): Promise<HAArea[]> {
  const response = await apiClient.get(`${HA_BASE}/instances/${instanceId}/areas`);
  return response.data?.areas || response.data || [];
}

// ── Service Calls ──

export async function callService(
  instanceId: string,
  domain: string,
  service: string,
  entityId: string,
  data?: Record<string, unknown>
): Promise<{ success: boolean }> {
  const response = await apiClient.post(
    `${HA_BASE}/instances/${instanceId}/services/${domain}/${service}`,
    { entity_id: entityId, data },
    {
      retryPolicy: {
        onRateLimit: 'skip',
      },
    }
  );
  return response.data;
}

// ── History ──

export async function getHistory(
  instanceId: string,
  entityId: string,
  period: string = '24h',
  start?: string,
  end?: string
): Promise<HAHistoryPoint[]> {
  const params = new URLSearchParams({ period });
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const response = await apiClient.get(
    `${HA_BASE}/instances/${instanceId}/history/${encodeURIComponent(entityId)}?${params}`
  );
  return response.data?.data || response.data || [];
}

// ── Automations ──

export async function getAutomations(instanceId: string): Promise<HAAutomationListItem[]> {
  const response = await apiClient.get(`${HA_BASE}/instances/${instanceId}/automations`);
  return response.data?.automations || response.data || [];
}

/**
 * Fetch one automation's full config plus the authoritative `editable` flag
 * and the SERVER-issued `baseHash`. The client must NOT recompute the hash —
 * it only echoes `baseHash` back on update for the drift check (Audit §A3).
 */
export async function getAutomation(
  instanceId: string,
  automationId: string
): Promise<{ config: HAAutomationConfig | null; editable: boolean; baseHash: string | null }> {
  const response = await apiClient.get(
    `${HA_BASE}/instances/${instanceId}/automations/${automationId}`
  );
  return {
    config: response.data?.config ?? null,
    editable: response.data?.editable === true,
    baseHash: response.data?.baseHash ?? null,
  };
}

export async function createAutomation(
  instanceId: string,
  config: HAAutomationConfig
): Promise<{ success: boolean; automationId?: string; backupWarning?: string }> {
  const response = await apiClient.post(
    `${HA_BASE}/instances/${instanceId}/automations`,
    { config }
  );
  return response.data;
}

export async function updateAutomation(
  instanceId: string,
  automationId: string,
  config: HAAutomationConfig,
  baseHash: string
): Promise<{ success: boolean; backupWarning?: string; healthWarning?: string }> {
  const response = await apiClient.put(
    `${HA_BASE}/instances/${instanceId}/automations/${automationId}`,
    { config, baseHash }
  );
  return response.data;
}

export async function deleteAutomation(
  instanceId: string,
  automationId: string
): Promise<{ success: boolean; backupWarning?: string }> {
  const response = await apiClient.delete(
    `${HA_BASE}/instances/${instanceId}/automations/${automationId}`
  );
  return response.data;
}

export async function setAutomationWriteEnabled(
  instanceId: string,
  enabled: boolean
): Promise<{ success: boolean; automationWriteEnabled: boolean }> {
  const response = await apiClient.patch(
    `${HA_BASE}/instances/${instanceId}`,
    { automationWriteEnabled: enabled }
  );
  return response.data;
}

// ── Conversion / device control by user names ──────────────────────────────

export type ConversionTier = 1 | 2 | 3;

export interface ConversionRecordView {
  recordId: string;
  canonicalName: string;
  aliases: string[];
  semanticType?: string;
  identity: {
    tier: ConversionTier;
    haNameSnapshot: string;
    anchors: { tier3: { entityId: string; domain: string } };
  };
  status: "active" | "stale";
  origin: string;
  updatedAt: string;
}

export interface ConversionOverlay {
  canonicalName: string;
  aliases: string[];
  semanticType?: string;
}

export interface ConversionView {
  records: ConversionRecordView[];
  syncedAt: string | null;
  overlay: ConversionOverlay[];
}

export async function getConversion(
  instanceId: string
): Promise<ConversionView> {
  const response = await apiClient.get(
    `${HA_BASE}/${instanceId}/conversion`
  );
  return {
    records: response.data?.records ?? [],
    syncedAt: response.data?.syncedAt ?? null,
    overlay: response.data?.overlay ?? [],
  };
}

export interface DeviceActionResult {
  status: "ok" | "ambiguous" | "missing" | "type_changed" | "unsupported";
  entityId?: string;
  stateAfter?: string;
  suggestSave?: boolean;
  message: string;
}

export type DeviceAction =
  | { kind: "on" }
  | { kind: "off" }
  | { kind: "set_brightness"; valuePct: number };

export async function controlDevice(
  instanceId: string,
  phrase: string,
  action: DeviceAction
): Promise<DeviceActionResult> {
  const response = await apiClient.post(
    `${HA_BASE}/${instanceId}/devices/control`,
    { phrase, action }
  );
  return response.data?.result;
}

export async function renameHaEntity(
  instanceId: string,
  entityId: string,
  name: string
): Promise<void> {
  await apiClient.post(`${HA_BASE}/${instanceId}/devices/rename`, {
    entityId,
    name,
  });
}

export async function upsertConversionOverlay(
  instanceId: string,
  overlay: ConversionOverlay
): Promise<void> {
  await apiClient.post(
    `${HA_BASE}/${instanceId}/conversion/overlay`,
    overlay
  );
}

export async function deleteConversionOverlay(
  instanceId: string,
  canonicalName: string
): Promise<void> {
  await apiClient.delete(
    `${HA_BASE}/${instanceId}/conversion/overlay/${encodeURIComponent(
      canonicalName
    )}`
  );
}
